import { orgStructure } from '@/mocks/expertsData';
import { img, readdy } from '@/utils/images';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function OrgStructure() {
  return (
    <section className="relative bg-white py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]">
        <img
          src={readdy('org-structure-bg-001')}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="px-6 md:px-10">
        <div className="max-w-7xl mx-auto">

          <ScrollReveal variant="fade-up">
            <div className="text-center mb-12 md:mb-16">
              <p className="text-brand-brown text-sm tracking-widest mb-3 font-medium">
                {orgStructure.sectionSubtitle}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-brand-deep leading-tight">
                {orgStructure.sectionTitle}
              </h2>
              <p className="text-gray-400 text-sm mt-3 max-w-xl mx-auto">
                {orgStructure.sectionDesc}
              </p>
            </div>
          </ScrollReveal>

          {/* Org Chart */}
          <div className="flex flex-col items-center">
            {/* Board Chairman Level */}
            <ScrollReveal variant="fade-up" delay={80}>
              <div className="relative mb-0">
                <div className="w-44 md:w-52 px-5 py-4 md:py-5 bg-brand-white border border-brand-brown/60 rounded-2xl text-center">
                  <div className="w-10 h-10 md:w-11 md:h-11 mx-auto mb-2 flex items-center justify-center rounded-xl bg-brand-brown text-white">
                    <i className="ri-building-line text-lg" />
                  </div>
                  <p className="text-brand-deep font-semibold text-sm md:text-base">董事会主席</p>
                  <p className="text-gray-400 text-[10px] md:text-xs mt-0.5">战略规划、组织任命和全面管理运营团队</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-px h-6 md:h-8 bg-brand-brown/60" />
                  <div className="w-2 h-2 border-r border-b border-brand-brown/60 rotate-45 -mt-[6px]" />
                </div>
              </div>
            </ScrollReveal>

            {/* Chairman Level */}
            <ScrollReveal variant="fade-up" delay={140}>
              <div className="relative mb-0">
                <div className="w-44 md:w-52 px-5 py-4 md:py-5 bg-brand-white border border-brand-deep/60 rounded-2xl text-center">
                  <div className="w-10 h-10 md:w-11 md:h-11 mx-auto mb-2 flex items-center justify-center rounded-xl bg-brand-deep text-white">
                    <i className="ri-vip-crown-line text-lg" />
                  </div>
                  <p className="text-brand-deep font-semibold text-sm md:text-base">董事长</p>
                  <p className="text-gray-400 text-[10px] md:text-xs mt-0.5">战略规划、组织任命和全面管理运营团队</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-px h-6 md:h-8 bg-brand-deep/60" />
                  <div className="w-2 h-2 border-r border-b border-brand-deep/60 rotate-45 -mt-[6px]" />
                </div>
              </div>
            </ScrollReveal>

            {/* CEO Level */}
            <ScrollReveal variant="fade-up" delay={200}>
              <div className="relative mb-0">
                <div className="w-44 md:w-52 px-5 py-4 md:py-5 bg-brand-white border border-brand-brown/60 rounded-2xl text-center">
                  <div className="w-10 h-10 md:w-11 md:h-11 mx-auto mb-2 flex items-center justify-center rounded-xl bg-brand-brown text-white">
                    <i className="ri-user-star-line text-lg" />
                  </div>
                  <p className="text-brand-deep font-semibold text-sm md:text-base">总裁</p>
                  <p className="text-gray-400 text-[10px] md:text-xs mt-0.5">全面负责经营管理</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-px h-6 md:h-8 bg-brand-brown/60" />
                  <div className="w-2 h-2 border-r border-b border-brand-brown/60 rotate-45 -mt-[6px]" />
                </div>
              </div>
            </ScrollReveal>

            {/* Horizontal bar + Departments in unified grid */}
            <ScrollReveal variant="scale-up" delay={250}>
              <div className="relative w-full max-w-5xl px-4 md:px-6">
                {/* Horizontal connecting bar */}
                <div className="border-t border-brand-deep/80 w-5/6 mx-auto" />

                {/* Unified grid: each column = arrow drop + department card */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 -mt-[1px]">
                  {orgStructure.departments.map((dept, i) => (
                    <div key={dept.id} className="flex flex-col items-center px-1.5 md:px-1.5">
                      {/* Arrow drop from horizontal bar */}
                      <div className="w-px h-6 md:h-8 bg-brand-deep/70" />
                      <div className="w-2 h-2 border-r border-b border-brand-deep/70 rotate-45 -mt-[6px] mb-0" />

                      {/* Department card */}
                      <ScrollReveal variant="scale-up" delay={300 + i * 80}>
                        <div className="group border border-brand-deep/40 bg-brand-white rounded-2xl p-3 md:p-4 text-center transition-all duration-300 hover:border-brand-deep hover:shadow-md cursor-default h-full flex flex-col justify-center">
                          <div className="w-9 h-9 md:w-10 md:h-10 mx-auto mb-2 flex items-center justify-center rounded-xl bg-brand-deep text-white">
                            <i className={`${dept.icon} text-sm md:text-base`} />
                          </div>
                          <p className="text-brand-deep font-semibold text-[11px] md:text-xs mb-1 whitespace-nowrap">
                            {dept.title}
                          </p>
                          <p className="text-gray-400 text-[9px] md:text-[10px] leading-relaxed line-clamp-2">
                            {dept.desc}
                          </p>
                        </div>
                      </ScrollReveal>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>

        </div>
      </div>
    </section>
  );
}